import { useEffect, useState } from 'preact/hooks';
import { api } from '../state';

type Activity = { id: number; type: 'form' | 'chat' | 'call' | 'sms'; office: string | null; occurredAt: string; summary: Record<string, any>; contactId: number | null; name: string | null; email: string | null; phone: string | null; zip: string | null };
type Summary = { days: number; byType: { type: string; n: number }[]; byOffice: { office: string | null; n: number }[]; chats: { outcome: string; n: number }[] };
const LABEL = { form: 'Form', chat: 'Chat', call: 'Call', sms: 'Text' } as const;
const when = (iso: string) => new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const phoneFmt = (p: string | null) => (p && p.length === 10 ? `(${p.slice(0, 3)}) ${p.slice(3, 6)}-${p.slice(6)}` : p ?? '');

function describe(a: Activity) {
  const s = a.summary;
  if (a.type === 'form') return [s.service, s.message].filter(Boolean).join(' — ') || 'Website form';
  if (a.type === 'chat') {
    const o = { answered: `Answered by ${s.agent}`, missed: 'Missed — nobody accepted', offline: 'Offline — nobody available', abandoned: 'Left before an answer' }[s.outcome as string] ?? s.outcome;
    return `${o}${s.waitSeconds != null ? ` · waited ${s.waitSeconds}s` : ''}`;
  }
  return JSON.stringify(s);
}

export function Leads() {
  const [filters, setFilters] = useState({ office: '', type: '', from: '', to: '', search: '' });
  const [data, setData] = useState<{ rows: Activity[]; total: number } | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [offices, setOffices] = useState<string[]>([]);
  const [contact, setContact] = useState<{ contact: any; timeline: Activity[] } | null>(null);

  useEffect(() => {
    api<string[]>('/offices').then(setOffices);
    api<Summary>('/summary?days=30').then(setSummary);
  }, []);
  useEffect(() => {
    const q = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    api<{ rows: Activity[]; total: number }>(`/activities?${q}`).then(setData);
  }, [filters]);

  const n = (type: string) => summary?.byType.find((t) => t.type === type)?.n ?? 0;
  const chatN = (o: string) => summary?.chats.find((c) => c.outcome === o)?.n ?? 0;
  const set = (k: keyof typeof filters) => (e: Event) => setFilters({ ...filters, [k]: (e.target as HTMLInputElement).value });

  return (
    <div class="page">
      <h1>Leads</h1>
      {summary && (
        <div class="stats">
          <div><strong>{n('form')}</strong><span>Form leads</span></div>
          <div><strong>{n('chat')}</strong><span>Chats</span></div>
          <div><strong>{chatN('answered')}</strong><span>Chats answered</span></div>
          <div><strong>{chatN('missed') + chatN('offline')}</strong><span>Chats missed / offline</span></div>
          <p class="muted small">Last {summary.days} days</p>
        </div>
      )}
      <div class="filters">
        <select onChange={set('office')}><option value="">All offices</option>{offices.map((o) => <option>{o}</option>)}</select>
        <select onChange={set('type')}><option value="">All sources</option><option value="form">Forms</option><option value="chat">Chats</option><option value="call">Calls</option></select>
        <input type="date" onChange={set('from')} aria-label="From" />
        <input type="date" onChange={set('to')} aria-label="To" />
        <input type="search" placeholder="Name, email, or phone" onInput={set('search')} />
      </div>
      <table class="table">
        <thead><tr><th>When</th><th>Contact</th><th>Source</th><th>Office</th><th>Details</th></tr></thead>
        <tbody>
          {data?.rows.map((a) => (
            <tr class="click" onClick={async () => a.contactId && setContact(await api(`/contacts/${a.contactId}`))}>
              <td>{when(a.occurredAt)}</td>
              <td><strong>{a.name ?? '—'}</strong><br /><span class="muted small">{[a.email, phoneFmt(a.phone)].filter(Boolean).join(' · ')}</span></td>
              <td><span class={`pill ${a.type}`}>{LABEL[a.type]}</span>{a.summary.testMode && <span class="pill test">Test</span>}</td>
              <td>{a.office ?? '—'}</td>
              <td class="clip">{describe(a)}</td>
            </tr>
          ))}
          {data && !data.rows.length && <tr><td colSpan={5} class="muted">Nothing matches these filters.</td></tr>}
        </tbody>
      </table>
      {data && <p class="muted small">{data.total} total</p>}
      {contact && (
        <div class="drawer" onClick={(e) => e.target === e.currentTarget && setContact(null)}>
          <div class="drawer-panel">
            <button class="link close" onClick={() => setContact(null)}>Close</button>
            <h2>{contact.contact.name ?? 'Unknown'}</h2>
            <p class="muted small">{[contact.contact.email, phoneFmt(contact.contact.phone), contact.contact.zip && `ZIP ${contact.contact.zip}`].filter(Boolean).join(' · ')}</p>
            <ol class="timeline">
              {contact.timeline.map((a) => (
                <li><span class={`pill ${a.type}`}>{LABEL[a.type]}</span> <strong>{when(a.occurredAt)}</strong> · {a.office ?? 'No office'}<br /><span class="muted small">{describe(a)}</span></li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}
